import React, { useState, useEffect } from 'react';
import ReactDOMServer from 'react-dom/server';
import {
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
  Chip,
  Box,
  Hidden,
} from '@material-ui/core';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ComposedChart,
  Legend,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import Skeleton from '@material-ui/lab/Skeleton';
import PrintIcon from '@material-ui/icons/Print';
import InfoIcon from '@material-ui/icons/Info';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import {
  format,
  subMonths,
  addMonths,
  isBefore,
  isSameMonth,
  startOfMonth,
} from 'date-fns';
import { DatePicker } from '@material-ui/pickers';
import PageContent from '../../../components/Main/Content/mainContent';
import useStorage from '../../../utils/useStorage';
import useApi from '../../../utils/useApi';
import { formattedCurrency } from '../../../utils/utils';
import UIContainer from '../../../components/UI/Container/UIContainer';
import UILogo from '../../../components/UI/Logo/UILogo';

const PlanningPrintTemplate = ({ prevData, nextData, trendData, metrics }) => {
  const [credencials] = useStorage('userAcess');
  const styles = {
    container: {
      fontFamily: "'Roboto', sans-serif",
      padding: '20px 30px', // Reduced top/bottom padding
      color: '#333',
      width: '100%',
      backgroundColor: '#fff',
    },
    header: {
      textAlign: 'center',
      marginBottom: '40px',
      borderBottom: '3px solid #1976d2',
      paddingBottom: '15px',
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1976d2',
      margin: '0 0 10px 0',
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      margin: 0,
    },
    summarySection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '15px',
      marginBottom: '30px',
      padding: '20px',
      backgroundColor: '#f5f5f5',
      borderRadius: '8px',
    },
    summaryCard: {
      padding: '15px',
      backgroundColor: '#fff',
      borderLeft: '4px solid #1976d2',
      borderRadius: '4px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    summaryLabel: {
      fontSize: '11px',
      color: '#666',
      textTransform: 'uppercase',
      marginBottom: '5px',
      fontWeight: '500',
    },
    summaryValue: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#1976d2',
    },
    summarySubtext: {
      fontSize: '11px',
      color: '#999',
      marginTop: '3px',
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: 'bold',
      marginTop: '20px', // Reduced from 30px
      marginBottom: '12px', // Reduced from 15px
      color: '#1976d2',
      borderBottom: '2px solid #1976d2',
      paddingBottom: '8px',
      display: 'flex',
      alignItems: 'center',
    },
    row: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px',
      marginBottom: '20px',
    },
    column: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
    },
    card: {
      border: '1px solid #e0e0e0',
      padding: '15px',
      borderRadius: '6px',
      backgroundColor: '#fafafa',
    },
    cardTitle: {
      fontSize: '13px',
      fontWeight: 'bold',
      color: '#555',
      marginBottom: '10px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    metricRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0',
      borderBottom: '1px solid #f0f0f0',
    },
    metricLabel: {
      fontSize: '13px',
      color: '#666',
    },
    metricValue: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#333',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '10px',
      border: '1px solid #e0e0e0',
    },
    th: {
      textAlign: 'left',
      padding: '12px',
      backgroundColor: '#1976d2',
      color: '#fff',
      fontSize: '13px',
      fontWeight: '600',
    },
    td: {
      padding: '8px 12px', // Reduced from 10px 12px
      borderBottom: '1px solid #f0f0f0',
      fontSize: '13px',
    },
    footer: {
      marginTop: '20px', // Reduced from 40px
      paddingTop: '15px', // Reduced from 20px
      borderTop: '1px solid #e0e0e0',
      fontSize: '10px',
      textAlign: 'center',
      color: '#999',
      pageBreakInside: 'avoid',
    },
    badge: {
      display: 'inline-block',
      padding: '3px 8px',
      borderRadius: '3px',
      fontSize: '10px',
      fontWeight: 'bold',
      marginLeft: '8px',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>{credencials.companyData?.pseudonimo} - Saúde/Performance</h1>
        <h2 style={styles.subtitle}>Sistema Duzz</h2>
        <h3 style={styles.subtitle}>
          Período de Referência: {metrics.monthSel}
        </h3>
      </div>

      {/* Summary Cards */}
      <div style={styles.summarySection}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>
            Total Previsto para Entrar{' '}
            <span
              style={{
                ...styles.badge,
                backgroundColor: '#fff3e0',
                color: '#1976d2',
              }}
            >
              PREVISÃO
            </span>
          </div>
          <div style={styles.summaryValue}>
            {formattedCurrency(metrics.expectedRevenue)}
          </div>
          <div style={styles.summarySubtext}>
            {formattedCurrency(metrics.forecastedNewSales)} em novas vendas no mês
          </div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>
            Lucro Previsto das Vendas{' '}
            <span
              style={{
                ...styles.badge,
                backgroundColor: '#fff3e0',
                color: '#1976d2',
              }}
            >
              PREVISÃO
            </span>
          </div>
          <div
            style={{
              ...styles.summaryValue,
              color: metrics.projectedMargin > 0 ? '#388e3c' : '#d32f2f',
            }}
          >
            {formattedCurrency(metrics.projectedMargin)}
          </div>
          <div style={styles.summarySubtext}>
            Margem de Lucro: {metrics.projectedMarginPercent.toFixed(2)}%
          </div>
        </div>

        {/* Retiradas / Pro-labores */}
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>
            Retiradas dos Donos / Sócios{' '}
            <span
              style={{
                ...styles.badge,
                backgroundColor: '#fff3e0',
                color: '#1976d2',
              }}
            >
              PREVISÃO
            </span>
          </div>
          <div style={{ ...styles.summaryValue, color: '#7b1fa2' }}>
            {formattedCurrency(metrics.forecastedWithdrawals)}
          </div>
          <div style={styles.summarySubtext}>Estimado</div>
        </div>

        {/* New Net Result (Cash Flow) */}
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>
            Sobra Prevista no Caixa{' '}
            <span
              style={{
                ...styles.badge,
                backgroundColor: '#fff3e0',
                color: '#1976d2',
              }}
            >
              PREVISÃO
            </span>
          </div>
          <div
            style={{
              ...styles.summaryValue,
              color: metrics.projectedNetResult > 0 ? '#388e3c' : '#d32f2f',
            }}
          >
            {formattedCurrency(metrics.projectedNetResult)}
          </div>
          <div style={styles.summarySubtext}>
            Sobra em %: {metrics.projectedNetMarginPercent.toFixed(2)}%
          </div>
        </div>

        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>
            Valor Médio por Venda{' '}
            <span
              style={{
                ...styles.badge,
                backgroundColor: '#fff3e0',
                color: '#1976d2',
              }}
            >
              PREVISÃO
            </span>
          </div>
          <div style={styles.summaryValue}>
            {formattedCurrency(metrics.ticketGoal)}
          </div>
          <div style={styles.summarySubtext}>Estimado</div>
        </div>

        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>
            Quantidade de Vendas{' '}
            <span
              style={{
                ...styles.badge,
                backgroundColor: '#fff3e0',
                color: '#1976d2',
              }}
            >
              PREVISÃO
            </span>
          </div>
          <div style={styles.summaryValue}>
            {metrics.avgVolume}
          </div>
          <div style={styles.summarySubtext}>Unidades</div>
        </div>

        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>
            Custo das Mercadorias (Estoque){' '}
            <span
              style={{
                ...styles.badge,
                backgroundColor: '#fff3e0',
                color: '#1976d2',
              }}
            >
              PREVISÃO
            </span>
          </div>
          <div style={{ ...styles.summaryValue, color: '#d32f2f' }}>
            {formattedCurrency(metrics.forecastedCMV)}
          </div>
          <div style={styles.summarySubtext}>Preço de custo dos itens vendidos</div>
        </div>

        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>
            Contas e Despesas da Loja{' '}
            <span
              style={{
                ...styles.badge,
                backgroundColor: '#fff3e0',
                color: '#1976d2',
              }}
            >
              PREVISÃO
            </span>
          </div>
          <div style={{ ...styles.summaryValue, color: '#d32f2f' }}>
            {formattedCurrency(metrics.recurringCosts)}
          </div>
          <div style={styles.summarySubtext}>Despesas fixas e contas do mês</div>
        </div>
      </div>

      {/* Summary Section */}
      <div style={{ ...styles.sectionTitle, pageBreakBefore: 'always' }}>
        📊 Resumo Comparativo{' '}
        <span
          style={{
            ...styles.badge,
            backgroundColor: '#fff3e0',
            color: '#1976d2',
          }}
        >
          PREVISÃO
        </span>
      </div>
      <div style={styles.row}>
        {/* Previous Cycle */}
        <div style={styles.column}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>
              Mês Anterior ({metrics.monthPrev})
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Vendas Realizadas</span>
              <span style={styles.metricValue}>
                {prevData?.actuals?.cash_flow?.count_sales || 0} un
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Valor Médio da Venda</span>
              <span style={styles.metricValue}>
                {formattedCurrency(prevData?.actuals?.avg_ticket || 0)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Recebido na Hora por Venda</span>
              <span style={{ ...styles.metricValue, color: '#1976d2' }}>
                {formattedCurrency(
                  prevData?.actuals?.avg_ticket_converted || 0,
                )}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Vendido a Prazo / Não Pago</span>
              <span style={{ ...styles.metricValue, color: '#d32f2f' }}>
                {(
                  100 -
                  (prevData?.actuals?.cash_flow?.conversion_rate || 0) * 100
                ).toFixed(1)}
                %
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Total que Entrou no Caixa</span>
              <span style={styles.metricValue}>
                {formattedCurrency(prevData?.actuals?.cash_flow?.total_revenue || 0)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Custo das Mercadorias (Estoque)</span>
              <span style={{ ...styles.metricValue, color: '#d32f2f' }}>
                {formattedCurrency(prevData?.actuals?.cogs || 0)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Contas e Gastos da Loja</span>
              <span style={{ ...styles.metricValue, color: '#d32f2f' }}>
                {formattedCurrency(prevData?.actuals?.consolidated_opex || 0)}
              </span>
            </div>
            <div
              style={{
                ...styles.metricRow,
                backgroundColor: '#e3f2fd',
                margin: '0px -15px 15px -15px',
                padding: '12px 15px',
                borderRadius: '0 0 6px 6px',
              }}
            >
              <span style={{ ...styles.metricLabel, fontWeight: 'bold' }}>
                Lucro das Vendas
              </span>
              <span style={{ ...styles.metricValue, color: prevData?.actuals?.cash_flow?.margin_value > 0 ? '#388e3c' : '#d32f2f' }}>
                {formattedCurrency(prevData?.actuals?.cash_flow?.margin_value || 0)} (
                {prevData?.actuals?.cash_flow?.margin_percent?.toFixed(2) || 0}%)
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Retiradas dos Donos / Sócios</span>
              <span style={{ ...styles.metricValue, color: '#7b1fa2' }}>
                {formattedCurrency(prevData?.actuals?.cash_flow?.withdrawals || 0)}
              </span>
            </div>

            <div
              style={{
                ...styles.metricRow,
                backgroundColor: '#e3f2fd',
                margin: '10px -15px -15px -15px',
                padding: '12px 15px',
                borderRadius: '0 0 6px 6px',
              }}
            >
              <span style={{ ...styles.metricLabel, fontWeight: 'bold' }}>
                Sobra Real no Caixa
              </span>
              <span style={{ ...styles.metricValue, color: prevData?.actuals?.cash_flow?.net_result > 0 ? '#388e3c' : '#d32f2f' }}>
                {formattedCurrency(prevData?.actuals?.cash_flow?.net_result || 0)} (
                {prevData?.actuals?.cash_flow?.net_margin_percent?.toFixed(2) || 0}%)
              </span>
            </div>
          </div>
        </div>

        {/* Current Cycle Projection */}
        <div style={styles.column}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Previsão do Mês ({metrics.monthSel})</div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Vendas Previstas</span>
              <span style={styles.metricValue}>{metrics.avgVolume} un</span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Valor Médio da Venda</span>
              <span style={styles.metricValue}>
                {formattedCurrency(metrics.ticketGoal)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Recebido na Hora por Venda</span>
              <span style={{ ...styles.metricValue, color: '#1976d2' }}>
                {formattedCurrency(
                  nextData?.forecast?.forecasted_avg_ticket_converted || 0,
                )}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Vendido a Prazo / Não Pago</span>
              <span style={{ ...styles.metricValue, color: '#d32f2f' }}>
                {(
                  100 - metrics.forecastedConversionRate * 100
                ).toFixed(1)}
                %
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Total Previsto para Entrar</span>
              <span style={styles.metricValue}>
                {formattedCurrency(metrics.expectedRevenue)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Custo das Mercadorias (Estoque)</span>
              <span style={{ ...styles.metricValue, color: '#d32f2f' }}>
                {formattedCurrency(metrics.forecastedCMV)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Contas e Gastos da Loja</span>
              <span style={{ ...styles.metricValue, color: '#d32f2f' }}>
                {formattedCurrency(metrics.recurringCosts)}
              </span>
            </div>
            <div
              style={{
                ...styles.metricRow,
                backgroundColor: '#e3f2fd',
                margin: '0px -15px 15px -15px',
                padding: '12px 15px',
                borderRadius: '0 0 6px 6px',
              }}
            >
              <span style={{ ...styles.metricLabel, fontWeight: 'bold' }}>
                Lucro Previsto das Vendas
              </span>
              <span style={{ ...styles.metricValue, color: metrics.projectedMargin > 0 ? '#388e3c' : '#d32f2f' }}>
                {formattedCurrency(metrics.projectedMargin)} (
                {metrics.projectedMarginPercent.toFixed(2) || 0}%)
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Retiradas dos Donos / Sócios</span>
              <span style={{ ...styles.metricValue, color: '#7b1fa2' }}>
                {formattedCurrency(metrics.forecastedWithdrawals)}
              </span>
            </div>

            <div
              style={{
                ...styles.metricRow,
                backgroundColor: '#e8f5e9',
                margin: '10px -15px -15px -15px',
                padding: '12px 15px',
                borderRadius: '0 0 6px 6px',
              }}
            >
              <span style={{ ...styles.metricLabel, fontWeight: 'bold' }}>
                Sobra Prevista no Caixa
              </span>
              <span
                style={{
                  ...styles.metricValue,
                  color: metrics.projectedNetResult > 0 ? '#388e3c' : '#d32f2f',
                }}
              >
                {formattedCurrency(metrics.projectedNetResult)} (
                {metrics.projectedNetMarginPercent.toFixed(2)}%)
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard RAIO-X: Current Cycle Realization */}
      <div style={{ ...styles.sectionTitle, pageBreakBefore: 'always' }}>
        🎯 Acompanhamento do Mês{' '}
        <span
          style={{
            ...styles.badge,
            backgroundColor: '#fff3e0',
            color: '#1976d2',
          }}
        >
          REAL vs PREVISÃO
        </span>
      </div>
      <div style={styles.row}>
        <div style={styles.column}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Entradas e Vendas</div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Total que Entrou no Caixa (Real)</span>
              <span style={styles.metricValue}>
                {formattedCurrency(nextData?.actuals?.cash_flow?.total_revenue || 0)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Total Previsto para Entrar</span>
              <span style={styles.metricValue}>
                {formattedCurrency(metrics.expectedRevenue)}
              </span>
            </div>
            <div style={{ ...styles.metricRow, borderBottom: 'none' }}>
              <span style={styles.metricLabel}>% da Meta Atingida</span>
              <span
                style={{
                  ...styles.metricValue,
                  color:
                    (nextData?.actuals?.cash_flow?.total_revenue || 0) >= metrics.expectedRevenue
                      ? '#388e3c'
                      : '#1976d2',
                }}
              >
                {metrics.forecastedNewSales > 0
                  ? (
                    ((nextData?.actuals?.cash_flow?.total_revenue || 0) /
                      metrics.expectedRevenue) *
                    100
                  ).toFixed(1)
                  : 0}
                %
              </span>
            </div>
          </div>
        </div>
        <div style={styles.column}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>
              Como Estão as Vendas{' '}
              <span
                style={{
                  ...styles.badge,
                  backgroundColor: '#e3f2fd',
                  color: '#1976d2',
                }}
              >
                REAL
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Vendas Realizadas</span>
              <span style={styles.metricValue}>
                {nextData?.actuals?.cash_flow?.count_sales || 0}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Valor Médio por Venda</span>
              <span style={styles.metricValue}>
                {formattedCurrency(nextData?.actuals?.avg_ticket || 0)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Recebido na Hora por Venda</span>
              <span style={{ ...styles.metricValue, color: '#1976d2' }}>
                {formattedCurrency(
                  nextData?.actuals?.avg_ticket_converted || 0,
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.row}>
        <div style={styles.column}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Lucro e Sobra no Caixa</div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Lucro das Vendas (Real)</span>
              <span style={styles.metricValue}>
                {formattedCurrency(nextData?.actuals?.cash_flow?.margin_value || 0)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Lucro das Vendas (Previsto)</span>
              <span style={styles.metricValue}>
                {formattedCurrency(metrics.projectedMargin)}
              </span>
            </div>

            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Retiradas dos Sócios (Real)</span>
              <span style={{ ...styles.metricValue, color: '#7b1fa2' }}>
                {formattedCurrency(nextData?.actuals?.cash_flow?.withdrawals || 0)}
              </span>
            </div>

            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Sobra Livre no Caixa (Real)</span>
              <span style={{ ...styles.metricValue, color: nextData?.actuals?.cash_flow?.net_result > 0 ? '#388e3c' : '#d32f2f' }}>
                {formattedCurrency(nextData?.actuals?.cash_flow?.net_result || 0)}
              </span>
            </div>
            <div style={{ ...styles.metricRow, borderBottom: 'none' }}>
              <span style={styles.metricLabel}>Sobra Livre no Caixa (Prevista)</span>
              <span style={{ ...styles.metricValue, color: metrics.projectedNetResult > 0 ? '#388e3c' : '#d32f2f' }}>
                {formattedCurrency(metrics.projectedNetResult)}
              </span>
            </div>
          </div>
        </div>
        <div style={styles.column}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Custos e Contas da Loja</div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Custo de Mercadorias (Real)</span>
              <span style={styles.metricValue}>
                {formattedCurrency(nextData?.actuals?.cogs || 0)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Custo de Mercadorias (Previsto)</span>
              <span style={styles.metricValue}>
                {formattedCurrency(metrics.forecastedCMV)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Contas Pagas da Loja (Real)</span>
              <span style={styles.metricValue}>
                {formattedCurrency(nextData?.actuals?.consolidated_opex || 0)}
              </span>
            </div>
            <div style={styles.metricRow}>
              <span style={styles.metricLabel}>Contas Estimadas da Loja</span>
              <span style={styles.metricValue}>
                {formattedCurrency(nextData?.forecast?.forecasted_opex || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Cash Flow Section */}
      <div style={styles.sectionTitle}>
        💰 Previsões para o Mês{' '}
        <span
          style={{
            ...styles.badge,
            backgroundColor: '#fff3e0',
            color: '#1976d2',
          }}
        >
          PREVISÃO
        </span>
      </div>
      <div style={styles.row}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Contas Antigas a Receber</div>
          <div
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#1976d2',
              marginBottom: '5px',
            }}
          >
            {formattedCurrency(metrics.activeReceivables)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Promissórias e parcelas de vendas passadas que vencem neste mês
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Mesmo Mês do Ano Passado</div>
          <div
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#7b1fa2',
              marginBottom: '5px',
            }}
          >
            {formattedCurrency(nextData?.last_year_summary?.exactly_month?.cash_flow?.total_revenue || 0)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Quanto entrou no caixa em {metrics.monthSel} do ano anterior
          </div>
        </div>
      </div>

      <div style={styles.row}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Previsão de Novas Vendas</div>
          <div
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#388e3c',
              marginBottom: '5px',
            }}
          >
            {formattedCurrency(metrics.forecastedNewSales)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Estimativa de novas vendas calculada pelo histórico da sua loja
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Lucro Previsto das Vendas</div>
          <div
            style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: metrics.projectedMargin > 0 ? '#388e3c' : '#d32f2f',
              marginBottom: '5px',
            }}
          >
            {formattedCurrency(metrics.projectedMargin)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Margem de lucro estimada:{' '}
            {metrics.projectedMarginPercent.toFixed(2)}%
          </div>
        </div>
      </div >

      {/* Default Rate Analysis Section */}
      < div style={{ ...styles.sectionTitle, pageBreakBefore: 'always' }}>
        🛒 Vendas à Vista vs. Prazo e Fiado{' '}
        <span
          style={{
            ...styles.badge,
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
          }}
        >
          REAL
        </span>
      </div >
      <div style={styles.row}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Vendas Pagas na Hora (À Vista)</div>
          <div
            style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#7b1fa2',
              marginBottom: '10px',
            }}
          >
            {((prevData?.actuals?.cash_flow?.conversion_rate || 0) * 100).toFixed(1)}%
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Porcentagem das vendas pagas no ato da compra
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>
            Período: {metrics.monthPrev}
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Vendas a Prazo / Não Pagas no Ato</div>
          <div
            style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#d32f2f',
              marginBottom: '10px',
            }}
          >
            {(100 - (prevData?.actuals?.cash_flow?.conversion_rate || 0) * 100).toFixed(1)}
            %
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Porcentagem das vendas feitas a prazo ou promissória
          </div>
          <div style={{ fontSize: '11px', color: '#999' }}>
            Valor que fica pendente para entrar no futuro
          </div>
        </div>
      </div>

      <div style={styles.row}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Valor por Compra: Total vs. Pago no Ato</div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
            }}
          >
            <div>
              <div
                style={{ fontSize: '11px', color: '#666', marginBottom: '3px' }}
              >
                Valor Total da Venda
              </div>
              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {formattedCurrency(prevData?.actuals?.avg_ticket || 0)}
              </div>
            </div>
            <div style={{ fontSize: '24px', color: '#ccc' }}>→</div>
            <div>
              <div
                style={{ fontSize: '11px', color: '#666', marginBottom: '3px' }}
              >
                Pago na Hora
              </div>
              <div
                style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#1976d2',
                }}
              >
                {formattedCurrency(
                  prevData?.actuals?.avg_ticket_converted || 0,
                )}
              </div>
            </div>
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Média que ficou a prazo por venda:{' '}
            {formattedCurrency(
              (prevData?.actuals?.avg_ticket || 0) -
              (prevData?.actuals?.avg_ticket_converted || 0),
            )}
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Total Vendido a Prazo</div>
          <div
            style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#d32f2f',
              marginBottom: '10px',
            }}
          >
            {formattedCurrency(
              prevData?.actuals?.cash_flow?.postponed_revenue !== undefined
                ? prevData?.actuals?.cash_flow?.postponed_revenue
                : (prevData?.actuals?.cash_flow?.total_sales || 0) *
                (1 - (prevData?.actuals?.cash_flow?.conversion_rate || 0)),
            )}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Vendas do período {metrics.monthPrev} que ficaram para receber no futuro
          </div>
        </div>
      </div>

      <div style={styles.sectionTitle}>
        📈 Tabela de Tendência (Fluxo de 6 Meses)
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Mês</th>
            <th style={styles.th}>Dinheiro que Entrou (Real)</th>
            <th style={styles.th}>Gastos Pagos (Real)</th>
            <th style={styles.th}>Previsto para Entrar</th>
            <th style={styles.th}>Gastos Previstos</th>
            <th style={styles.th}>Situação</th>
          </tr>
        </thead>
        <tbody>
          {trendData &&
            trendData.map((item, index) => (
              <tr key={index}>
                <td style={styles.td}>{item.name}</td>
                <td style={styles.td}>
                  {item.actualRevenue !== null
                    ? formattedCurrency(item.actualRevenue)
                    : '-'}
                </td>
                <td style={styles.td}>
                  {item.actualCosts !== null
                    ? formattedCurrency(item.actualCosts)
                    : '-'}
                </td>
                <td style={styles.td}>
                  {item.forecastRevenue !== null
                    ? formattedCurrency(item.forecastRevenue)
                    : '-'}
                </td>
                <td style={styles.td}>
                  {item.forecastCosts !== null
                    ? formattedCurrency(item.forecastCosts)
                    : '-'}
                </td>
                <td style={styles.td}>
                  <span
                    style={{
                      ...styles.badge,
                      backgroundColor: item.isFuture ? '#fff3e0' : '#e3f2fd',
                      color: item.isFuture ? '#1976d2' : '#1976d2',
                      marginLeft: 0,
                    }}
                  >
                    {item.isFuture ? 'PREVISÃO' : 'REAL'}
                  </span>
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      {/* Footer */}
      <div style={styles.footer}>
        <div>
          Relatório gerado automaticamente em{' '}
          {new Date().toLocaleString('pt-BR')}
        </div>
      </div>
    </div >
  );
};

const printPlanningReport = (data) => {
  const componentHtml = ReactDOMServer.renderToStaticMarkup(
    <PlanningPrintTemplate {...data} />,
  );

  const iframe = document.createElement('iframe');
  Object.assign(iframe.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    border: '0',
  });

  document.body.appendChild(iframe);

  const content = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Relatório de Planejamento</title>
            <style>
                @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap');
                body { font-family: 'Roboto', sans-serif; margin: 0; padding: 20px; }
                @media print {
                    @page { size: landscape; margin: 10mm; }
                    body { -webkit-print-color-adjust: exact; }
                }
            </style>
        </head>
        <body>
            ${componentHtml}
        </body>
        </html>
    `;

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(content);
  doc.close();

  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      setTimeout(() => {
        if (document.body.contains(iframe)) document.body.removeChild(iframe);
      }, 1000);
    }, 500);
  };
};

const PlanningCard = ({ title, values, type = 'display' }) => {
  return (
    <Card style={{ height: '100%' }}>
      <CardContent>
        <Typography color="textSecondary" gutterBottom>
          {title}
        </Typography>
        {values.map((v, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            <Typography variant="body2" component="p">
              {v.label}
            </Typography>
            <Typography variant="h5" component="h2" style={{ color: v.color }}>
              {v.value}
            </Typography>
            {v.subtext && (
              <Typography color="textSecondary" variant="caption">
                {v.subtext}
              </Typography>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const Planejamento = () => {
  const [selectedDate, setSelectedDate] = useState(startOfMonth(new Date()));
  const prevDate = subMonths(selectedDate, 1);

  const [planningData, setPlanningData] = useState(null);
  const [goals, setGoals] = useState({
    ticket_goal: 0,
    margin_goal: 0,
    cmv_estimated: 0,
    month: '',
  });

  const [fetchData, { loading, data }] = useApi({
    method: 'get',
    url: '/planning',
  });

  const [saveGoals, { loading: saving }] = useApi({
    method: 'post',
    url: '/planning',
  });

  useEffect(() => {
    const monthsToFetch = [];
    // Fetch 4 previous months + current month + 1 future month
    for (let i = -4; i <= 1; i++) {
      const d = addMonths(selectedDate, i);
      monthsToFetch.push(format(d, 'yyyy-MM'));
    }

    fetchData({
      params: {
        months: monthsToFetch,
      },
    });
  }, [selectedDate]);

  useEffect(() => {
    if (data) {
      setPlanningData(data);
      const monthSel = format(selectedDate, 'yyyy-MM');
      const result = data[monthSel];
      // Goals/Suggestion Logic is handled by API result predominantly now
    }
  }, [data, selectedDate]);

  const handleGoalChange = (event) => {
    setGoals({
      ...goals,
      [event.target.name]: event.target.value,
    });
  };

  const handleSave = async () => {
    await saveGoals({
      data: {
        ...goals,
        month: format(selectedDate, 'yyyy-MM'),
      },
    });
    // Refresh?
    const monthPrev = format(prevDate, 'yyyy-MM');
    const monthSel = format(selectedDate, 'yyyy-MM');
    fetchData({
      params: {
        months: [monthPrev, monthSel],
      },
    });
  };

  const handlePrint = () => {
    const monthPrevStr = format(prevDate, 'yyyy-MM');
    const monthSelStr = format(selectedDate, 'yyyy-MM');

    printPlanningReport({
      prevData: planningData ? planningData[monthPrevStr] : null,
      nextData: planningData ? planningData[monthSelStr] : null,
      trendData: trendData,
      metrics: {
        monthPrev: format(prevDate, 'MM/yyyy'),
        monthSel: format(selectedDate, 'MM/yyyy'),
        ticketGoal: forecastedTicketGoal,
        avgVolume: forecastedAvgCountSales,
        forecastedTotalRevenue,
        forecastedNewSales,
        forecastedCMV,
        recurringCosts: forecastedRecurringCosts,
        forecastedWithdrawals,
        activeReceivables: forecastedActiveReceivables,
        expectedRevenue: forecastedTotalRevenue,

        projectedMargin: forecastedMarginValue,
        projectedMarginPercent: forecastedMarginPercent,

        projectedNetResult: forecastedNetResult,
        projectedNetMarginPercent: forecastedNetMarginPercent,

        forecastedConversionRate,
      },
    });
  };

  if (loading) {
    return (
      <PageContent>
        <div style={{ width: '100%', padding: '0 8px' }}>
          {/* Skeleton for Scoreboard */}
          <Paper style={{ padding: '15px 10px', marginBottom: 20 }}>
            <Grid container spacing={2} style={{ marginBottom: 15 }}>
              <Grid item>
                <Skeleton variant="text" width={200} height={40} />
              </Grid>
              <Grid item>
                <Skeleton
                  variant="rect"
                  width={120}
                  height={40}
                  style={{ borderRadius: 4 }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1.5}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Grid item xs={6} sm={6} md={2.4} key={i}>
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={20}
                    style={{ marginBottom: 5 }}
                  />
                  <Skeleton
                    variant="text"
                    width="60%"
                    height={32}
                    style={{ marginBottom: 5 }}
                  />
                  <Skeleton variant="text" width="50%" height={16} />
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Grid container spacing={3}>
            {/* Skeleton for Bullet Charts */}
            <Grid item xs={12} md={8}>
              <Paper style={{ padding: 20 }}>
                <Skeleton
                  variant="text"
                  width="40%"
                  height={32}
                  style={{ marginBottom: 20 }}
                />
                <Divider style={{ marginBottom: 20 }} />
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} style={{ marginBottom: 20 }}>
                    <Skeleton
                      variant="text"
                      width="30%"
                      height={20}
                      style={{ marginBottom: 10 }}
                    />
                    <Skeleton
                      variant="rect"
                      height={20}
                      style={{ borderRadius: 4 }}
                    />
                  </div>
                ))}
              </Paper>
            </Grid>

            {/* Skeleton for Efficiency */}
            <Grid item xs={12} md={4}>
              <Paper style={{ padding: 20 }}>
                <Skeleton
                  variant="text"
                  width="60%"
                  height={32}
                  style={{ marginBottom: 20 }}
                />
                <Divider style={{ marginBottom: 20 }} />
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} style={{ marginBottom: 15 }}>
                    <Skeleton variant="text" width="50%" height={20} />
                    <Skeleton variant="text" width="70%" height={32} />
                  </div>
                ))}
              </Paper>
            </Grid>

            {/* Skeleton for Trend Chart */}
            <Grid item xs={12}>
              <Paper style={{ padding: 20 }}>
                <Skeleton
                  variant="text"
                  width="40%"
                  height={32}
                  style={{ marginBottom: 20 }}
                />
                <Skeleton
                  variant="rect"
                  height={300}
                  style={{ borderRadius: 4 }}
                />
              </Paper>
            </Grid>
          </Grid>
        </div>
      </PageContent>
    );
  }

  const monthPrevStr = format(prevDate, 'yyyy-MM');
  const monthSelStr = format(selectedDate, 'yyyy-MM');

  const prevData = planningData ? planningData[monthPrevStr] : null;
  const nextData = planningData ? planningData[monthSelStr] : null;

  // Live Calculations
  // Live Calculations
  const forecastedAvgCountSales = nextData?.forecast?.cash_flow?.forecasted_sales_count || 0;
  const forecastedTicketGoal = parseFloat(
    nextData?.forecast?.forecasted_avg_ticket || 0,
  );
  const forecastedRecurringCosts = nextData?.forecast?.forecasted_opex || 0;
  const forecastedActiveReceivables =
    nextData?.forecast?.cash_flow?.pending_receivables || 0;
  const actualReceivedReceivables =
    nextData?.actuals?.cash_flow?.received_receivables || 0;

  const forecastedPostponedRevenue =
    nextData?.forecast?.cash_flow?.forecasted_postponed_revenue || 0;
  const actualPostponedRevenue =
    nextData?.actuals?.cash_flow?.postponed_revenue !== undefined
      ? nextData?.actuals?.cash_flow?.postponed_revenue
      : Math.max(
        0,
        (nextData?.actuals?.cash_flow?.total_sales || 0) -
        (nextData?.actuals?.cash_flow?.total_paid || 0),
      );

  const forecastedConversionRate =
    nextData?.forecast?.cash_flow?.conversion_rate || 0;

  const financialHealth =
    nextData?.financial_health ||
    nextData?.actuals?.financial_health ||
    null;

  const idlScore = financialHealth?.idl_score ?? 100;
  const idlStatus = financialHealth?.status ?? 'Equilibrado';
  const idlColor = financialHealth?.status_color ?? '#3B82F6';
  const idlDiagnosis = financialHealth?.diagnosis ?? '';
  const fhMetrics = financialHealth?.metrics ?? {
    expansion_ratio: actualReceivedReceivables > 0 ? (actualPostponedRevenue / actualReceivedReceivables).toFixed(2) : 1,
    collection_efficiency: forecastedActiveReceivables > 0 ? (actualReceivedReceivables / forecastedActiveReceivables).toFixed(2) : 1,
    leverage_rate: (nextData?.actuals?.cash_flow?.total_sales || 0) > 0 ? (actualPostponedRevenue / nextData?.actuals?.cash_flow?.total_sales).toFixed(2) : 0,
    past_dependence_rate: (nextData?.actuals?.cash_flow?.total_revenue || 0) > 0 ? (actualReceivedReceivables / nextData?.actuals?.cash_flow?.total_revenue).toFixed(2) : 0,
    working_capital_delta: actualPostponedRevenue - actualReceivedReceivables,
  };
  const fhAlerts = financialHealth?.alerts ?? [];

  // 1. Competence

  // 2. Cash Flow
  const forecastedTotalRevenue =
    nextData?.forecast?.cash_flow?.forecasted_revenue || 0;
  const forecastedNewSales =
    nextData?.forecast?.cash_flow?.forecasted_sales_value || 0;

  const forecastedCMV = nextData?.forecast?.forecasted_cogs || 0;

  // User inputs (goals) are now for comparison only.
  // Metrics are derived strictly from System Estimates (API).
  const forecastedMarginValue =
    nextData?.forecast?.cash_flow?.forecasted_margin_value || 0;
  const forecastedMarginPercent =
    nextData?.forecast?.cash_flow?.forecasted_margin_percent || 0;

  const forecastedWithdrawals =
    nextData?.forecast?.cash_flow?.forecasted_withdrawals || 0;

  const forecastedNetResult =
    nextData?.forecast?.cash_flow?.forecasted_net_result || 0;
  const forecastedNetMarginPercent =
    nextData?.forecast?.cash_flow?.forecasted_net_margin_percent || 0;

  const scoreboardData = [
    {
      label: 'Total que Entrou no Caixa (R$)',
      value: nextData?.actuals?.cash_flow?.total_revenue || 0,
      goal: forecastedTotalRevenue,
      type: 'currency',
      color: '#1976d2',
    },
    {
      label: 'Sobra Real no Caixa (R$)',
      value: nextData?.actuals?.cash_flow?.net_result || 0,
      goal: forecastedNetResult,
      type: 'currency',
      color: '#388e3c',
    },
    {
      label: 'Pago na Hora (À Vista)',
      value: nextData?.actuals?.cash_flow?.conversion_rate * 100,
      goal: forecastedConversionRate * 100,
      type: 'percent',
      color: '#7b1fa2',
    },
    {
      label: 'Valor Médio por Venda',
      value: nextData?.actuals?.avg_ticket || 0,
      prevValue: prevData?.actuals?.avg_ticket || 0,
      type: 'currency',
      color: '#555',
    },
  ];

  // Prepare Trend Data (4 months back to 1 month ahead)
  const trendData = [];
  const today = startOfMonth(new Date());

  for (let i = -4; i <= 1; i++) {
    const d = addMonths(today, i);
    const k = format(d, 'yyyy-MM');
    const monthData = planningData ? planningData[k] : null;

    const isFuture = isBefore(today, d);

    // Actual values (only for past and current)
    const actualRevenue = !isFuture ? monthData?.actuals?.cash_flow?.total_revenue || 0 : null;
    const actualCosts = !isFuture
      ? (monthData?.actuals?.cogs || 0) +
      (monthData?.actuals?.consolidated_opex || 0)
      : null;

    const forecastRevenue = monthData?.forecast?.cash_flow?.forecasted_revenue || 0

    const forecastCosts = (monthData?.forecast?.forecasted_cogs || 0) +
      (monthData?.forecast?.forecasted_opex || 0)

    const actualTicket = !isFuture ? monthData?.actuals?.avg_ticket || 0 : 0;
    const actualTicketEffective = !isFuture ? monthData?.actuals?.avg_ticket_converted || 0 : 0;

    const forecastTicket = parseFloat(monthData?.forecast?.forecasted_avg_ticket || 0);
    const forecastTicketEffective = monthData?.forecast?.forecasted_avg_ticket_converted || 0;

    const actualWithdrawals = !isFuture
      ? monthData?.actuals?.cash_flow?.withdrawals || 0
      : null;
    const forecastWithdrawals = isFuture
      ? monthData?.forecast?.cash_flow?.forecasted_withdrawals || 0
      : null;

    trendData.push({
      name: format(d, 'MMM'),
      actualRevenue,
      actualCosts,
      actualWithdrawals,
      forecastRevenue,
      forecastCosts,
      forecastWithdrawals,
      actualTicket,
      actualTicketEffective,
      forecastTicket,
      forecastTicketEffective,
      withdrawals: actualWithdrawals,
      isFuture,
    });
  }

  const BulletChart = ({
    label,
    goal,
    actual,
    colorString,
    isPercent = false,
  }) => {
    const max = Math.max(goal, actual) * 1.2 || 100;
    const goalPercent = Math.min((goal / max) * 100, 100);
    const actualPercent = Math.min((actual / max) * 100, 100);
    const formatter = isPercent
      ? (v) => `${Number(v).toFixed(1)}%`
      : formattedCurrency;

    return (
      <div style={{ marginBottom: 15 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 5,
          }}
        >
          <Typography
            variant="body2"
            style={{ fontWeight: 'bold', color: '#555' }}
          >
            {label}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Estimado: {formatter(goal)} | Real: {formatter(actual)}
          </Typography>
        </div>
        <div
          style={{
            position: 'relative',
            height: 20,
            backgroundColor: '#eee',
            borderRadius: 4,
          }}
        >
          {/* Goal Marker (Background Bar) */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${goalPercent}%`,
              borderRight: '2px solid #999',
              backgroundColor: '#e0e0e0',
              zIndex: 1,
            }}
          />

          {/* Actual Bar */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: '25%',
              height: '50%',
              width: `${actualPercent}%`,
              backgroundColor:
                actual > goal && colorString === '#d32f2f'
                  ? '#d32f2f'
                  : colorString, // Red if cost exceeds
              zIndex: 2,
              borderRadius: 2,
            }}
          />
        </div>
      </div>
    );
  };

  return (
    <PageContent>
      <div style={{ width: '100%', padding: '0 8px' }}>
        {/* ZONE 1: PLACAR (SCOREBOARD) */}
        <Paper id="tour-planning-scoreboard" style={{ padding: '15px 10px', marginBottom: 20 }}>
          <Grid
            container
            spacing={2}
            style={{
              marginBottom: 15,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid
              item
              id="tour-planning-title"
              style={{ display: 'flex', alignItems: 'center', gap: 15, flexWrap: 'wrap' }}
            >
              <UILogo name="Saúde&" lastName="Performance" size={25} />

              <Tooltip
                title="As estimativas são calculadas com base no comportamento histórico dos últimos 6 meses e nas vendas já agendadas para o ciclo atual."
                arrow
              >
                <Box id="tour-planning-estimates-expl" display="flex" alignItems="center" color="text.secondary" style={{ cursor: 'help' }}>
                  <InfoIcon fontSize="small" />
                  <Hidden xsDown>
                    <Typography variant="caption" style={{ marginLeft: 4 }}>Saiba mais sobre as projeções</Typography>
                  </Hidden>
                </Box>
              </Tooltip>

              <Tooltip title="Imprimir Relatório">
                <IconButton id="tour-planning-print" color="primary" onClick={handlePrint} size="small">
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
          <Grid container spacing={2} alignItems="stretch">
            {[
              {
                label: 'TOTAL QUE ENTROU NO CAIXA',
                value: nextData?.actuals?.cash_flow?.total_revenue || 0,
                subtext: 'Dinheiro que entrou no mês',
                color: '#1976d2',
                tag: 'ENTROU',
                tagColor: '#e3f2fd',
                tagTextColor: '#1976d2'
              },
              {
                label: 'LUCRO DAS VENDAS',
                value: nextData?.actuals?.cash_flow?.margin_value || 0,
                subtext: `Sobrou ${(nextData?.actuals?.cash_flow?.margin_percent || 0).toFixed(1)}% das vendas`,
                color: (nextData?.actuals?.cash_flow?.margin_value || 0) > 0 ? '#388e3c' : '#d32f2f',
                tag: 'SOBROU',
                tagColor: '#e3f2fd',
                tagTextColor: (nextData?.actuals?.cash_flow?.margin_value || 0) > 0 ? '#388e3c' : '#d32f2f'
              },
              {
                label: 'RETIRADAS DOS DONOS',
                value: nextData?.actuals?.cash_flow?.withdrawals || 0,
                subtext: 'Pró-labore e saídas dos sócios',
                color: '#7b1fa2',
                tag: 'RETIRADA',
                tagColor: '#f3e5f5',
                tagTextColor: '#7b1fa2'
              },
              {
                label: 'SOBRA FINAL NO CAIXA',
                value: nextData?.actuals?.cash_flow?.net_result || 0,
                subtext: `Dinheiro livre que ficou na loja`,
                color: (nextData?.actuals?.cash_flow?.net_result || 0) > 0 ? '#388e3c' : '#d32f2f',
                tag: 'NO CAIXA',
                tagColor: '#e8f5e9',
                tagTextColor: (nextData?.actuals?.cash_flow?.net_result || 0) > 0 ? '#388e3c' : '#d32f2f'
              },
              {
                label: 'RECEBIDO NO ATO (À VISTA)',
                value: (nextData?.actuals?.cash_flow?.conversion_rate || 0) * 100,
                isPercent: true,
                subtext: `Meta esperada: ${(forecastedConversionRate * 100).toFixed(1)}%`,
                color: '#7b1fa2',
                tag: 'À VISTA',
                tagColor: '#e3f2fd',
                tagTextColor: '#7b1fa2'
              },
              {
                label: 'VALOR MÉDIO DA COMPRA',
                value: nextData?.actuals?.avg_ticket || 0,
                subtext: `${nextData?.actuals?.cash_flow?.count_sales || 0} compras realizadas`,
                color: '#555',
                tag: 'POR CLIENTE',
                tagColor: '#e3f2fd',
                tagTextColor: '#555'
              },
            ].map((item, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <div
                  style={{
                    padding: '15px',
                    backgroundColor: '#fff',
                    borderLeft: `4px solid ${item.color}`,
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', marginBottom: '8px', fontWeight: '500', display: 'flex', alignItems: 'center' }}>
                    {item.label}
                    {item.tag && (
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        marginLeft: '8px',
                        backgroundColor: item.tagColor,
                        color: item.tagTextColor
                      }}>
                        {item.tag}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: item.color, marginBottom: '4px' }}>
                    {item.isPercent ? `${item.value.toFixed(1)}%` : formattedCurrency(item.value)}
                  </div>
                  <div style={{ fontSize: '11px', color: '#999' }}>
                    {item.subtext}
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>

          {/* ALERTAS & SAÚDE DA duxIA */}
          <div style={{ marginTop: 25, paddingTop: 15, borderTop: '1px solid #e0e0e0', padding: '0 5px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 15 }}>
              <Chip
                label={`Nota de Saúde: ${idlScore}/100 • ${idlStatus}`}
                size="small"
                style={{
                  backgroundColor: idlColor,
                  color: '#fff',
                  fontWeight: 'bold',
                  fontSize: '12px'
                }}
              />
              {idlDiagnosis && (
                <Typography variant="caption" style={{ color: '#666', fontStyle: 'italic' }}>
                  {idlDiagnosis}
                </Typography>
              )}
            </div>

            {/* Lista de Alertas Ativos da duxIA */}
            {fhAlerts.length > 0 && (
              <div>
                <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: '#444', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                  ⚠️ Alertas Inteligentes da duxIA ({fhAlerts.length} identificados):
                </Typography>
                <Grid container spacing={2}>
                  {fhAlerts.map((alert, idx) => (
                    <Grid item xs={12} md={fhAlerts.length === 1 ? 12 : 6} key={idx}>
                      <div style={{
                        backgroundColor: alert.level === 'danger' ? '#fff5f5' : '#fffbf0',
                        borderLeft: `4px solid ${alert.level === 'danger' ? '#e53e3e' : '#dd6b20'}`,
                        borderRadius: '0 8px 8px 0',
                        padding: '12px 16px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                            <Typography variant="subtitle2" style={{ fontWeight: 'bold', color: alert.level === 'danger' ? '#c53030' : '#c05621' }}>
                              {alert.name}
                            </Typography>
                            <Chip
                              label={alert.severity || (alert.level === 'danger' ? 'Alta' : 'Média')}
                              size="small"
                              style={{
                                height: 18,
                                fontSize: 10,
                                backgroundColor: alert.level === 'danger' ? '#fed7d7' : '#feebc8',
                                color: alert.level === 'danger' ? '#9b2c2c' : '#7b341e',
                                fontWeight: 'bold'
                              }}
                            />
                          </div>
                          <Typography variant="body2" style={{ color: '#4a5568', fontSize: '13px', marginBottom: 8 }}>
                            {alert.message}
                          </Typography>
                        </div>
                        <div style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.7)',
                          borderRadius: 4,
                          padding: '6px 10px',
                          marginTop: 6,
                          border: '1px dashed rgba(0, 0, 0, 0.1)'
                        }}>
                          <Typography variant="caption" style={{ fontWeight: 'bold', color: '#2d3748', display: 'block' }}>
                            💡 Ação Recomendada:
                          </Typography>
                          <Typography variant="caption" style={{ color: '#4a5568' }}>
                            {alert.action}
                          </Typography>
                        </div>
                      </div>
                    </Grid>
                  ))}
                </Grid>
              </div>
            )}
          </div>
        </Paper>

        <Grid container spacing={3}>
          {/* ZONE 2: RAIO-X (Main Body) */}
          <Grid item xs={12} md={8}>
            <Paper id="tour-planning-raiox" style={{ padding: 20, height: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <Typography variant="h6" style={{ color: '#555' }}>
                  Acompanhamento do Mês (Raio-X)
                </Typography>
                <Chip
                  label="REAL vs PREVISTO"
                  size="small"
                  style={{
                    backgroundColor: '#fff3e0',
                    color: '#1976d2',
                    fontWeight: 'bold',
                  }}
                />
              </div>
              <Divider style={{ marginBottom: 20 }} />

              <BulletChart
                label="Total que Entrou no Caixa"
                goal={forecastedTotalRevenue}
                actual={nextData?.actuals?.cash_flow?.total_revenue || 0}
                colorString="#1976d2"
              />
              <BulletChart
                label="Lucro das Vendas (Antes das Retiradas)"
                goal={forecastedMarginValue}
                actual={nextData?.actuals?.cash_flow?.margin_value || 0}
                colorString={(nextData?.actuals?.cash_flow?.margin_value || 0) >= 0 ? "#388e3c" : "#d32f2f"}
              />
              <BulletChart
                label="Sobra Livre no Caixa (Após Retiradas dos Donos)"
                goal={forecastedNetResult}
                actual={nextData?.actuals?.cash_flow?.net_result || 0}
                colorString={(nextData?.actuals?.cash_flow?.net_result || 0) >= 0 ? "#388e3c" : "#d32f2f"}
              />
              <BulletChart
                label="Contas e Gastos da Loja (Luz, Aluguel, Equipe)"
                goal={forecastedRecurringCosts}
                actual={nextData?.actuals?.consolidated_opex || 0}
                colorString="#f57c00"
              />
              <BulletChart
                label="Custo das Mercadorias Vendidas (Reposição)"
                goal={forecastedCMV}
                actual={nextData?.actuals?.cogs || 0}
                colorString="#d32f2f"
              />
              <BulletChart
                label="Contas Antigas / Fiado Recebido (Passado)"
                goal={forecastedActiveReceivables || 0}
                actual={actualReceivedReceivables}
                colorString="#7b1fa2"
              />
              <BulletChart
                label="Fiado Novo / Vendas a Prazo Feitas Neste Mês"
                goal={forecastedPostponedRevenue || 0}
                actual={actualPostponedRevenue}
                colorString="#e65100"
              />
            </Paper>
          </Grid>

          {/* ZONE 3: OPERACIONAL (Bottom/Side) */}
          <Grid item xs={12} md={4}>
            <Paper id="tour-planning-efficiency" style={{ padding: 20, height: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <Typography variant="h6" style={{ color: '#555' }}>
                  Desempenho das Vendas
                </Typography>
                <Chip
                  label="REAL"
                  size="small"
                  style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    fontWeight: 'bold',
                  }}
                />
              </div>
              <Divider style={{ marginBottom: 20 }} />
              <Grid container spacing={2} style={{ textAlign: 'center' }}>
                <Grid item xs={12}>
                  <Typography variant="body2">Compras Realizadas na Loja</Typography>
                  <Typography variant="h5">
                    {nextData?.actuals?.cash_flow?.count_sales || 0}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <ArrowDownwardIcon style={{ color: '#ccc' }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">Valor Médio por Compra</Typography>
                  <Typography variant="h5">
                    {formattedCurrency(nextData?.actuals?.avg_ticket || 0)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <ArrowDownwardIcon style={{ color: '#ccc' }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">Dinheiro Pago na Hora por Venda</Typography>
                  <Typography variant="h5" style={{ color: '#1976d2' }}>
                    {formattedCurrency(
                      nextData?.actuals?.avg_ticket_converted || 0,
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    O que entrou no ato da compra
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <ArrowDownwardIcon style={{ color: '#ccc' }} />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">% que Vira Lucro no Caixa</Typography>
                  <Typography variant="h5" style={{ color: '#388e3c' }}>
                    {(nextData?.actuals?.cash_flow?.margin_percent || 0).toFixed(1)}%
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* ZONE 4: INADIMPLÊNCIA ANALYSIS */}
          <Grid item xs={12}>
            <Paper id="tour-planning-inadimplencia" style={{ padding: 20 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <Typography variant="h6" style={{ color: '#555' }}>
                  🛒 Vendas à Vista vs. Prazo e Fiado
                </Typography>
                <Chip
                  label="REAL"
                  size="small"
                  style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    fontWeight: 'bold',
                  }}
                />
              </div>
              <Divider style={{ marginBottom: 20 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    RECEBIDO NO ATO (À VISTA)
                  </Typography>
                  <Typography
                    variant="h4"
                    style={{ color: '#7b1fa2', marginTop: 5 }}
                  >
                    {((nextData?.actuals?.cash_flow?.conversion_rate || 0) * 100).toFixed(
                      1,
                    )}
                    %
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Vendas que viraram dinheiro na hora
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    VENDIDO A PRAZO / FIADO
                  </Typography>
                  <Typography
                    variant="h4"
                    style={{
                      color:
                        (100 - (nextData?.actuals?.cash_flow?.conversion_rate || 0) * 100) <= 40
                          ? '#2e7d32'
                          : '#d32f2f',
                      marginTop: 5,
                    }}
                  >
                    {(
                      100 -
                      (nextData?.actuals?.cash_flow?.conversion_rate || 0) * 100
                    ).toFixed(1)}
                    %
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Vendas para receber nos próximos meses
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    FICOU A PRAZO POR COMPRA
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ color: '#d32f2f', marginTop: 5 }}
                  >
                    {formattedCurrency(
                      (nextData?.actuals?.avg_ticket || 0) -
                      (nextData?.actuals?.avg_ticket_converted || 0),
                    )}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Média que cada cliente deixou para depois
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    TOTAL VENDIDO A PRAZO NESTE MÊS
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ color: '#d32f2f', marginTop: 5 }}
                  >
                    {formattedCurrency(actualPostponedRevenue)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Total deste mês que ficou para receber no futuro
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider style={{ margin: '10px 0 15px 0' }} />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    DÍVIDAS VELHAS RECEBIDAS
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ color: '#7b1fa2', marginTop: 5 }}
                  >
                    {formattedCurrency(actualReceivedReceivables)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Contas de meses passados pagas agora
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    SALDO DO DINHEIRO NA RUA
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{
                      color: (fhMetrics.working_capital_delta || 0) > 0 ? '#d32f2f' : '#2e7d32',
                      marginTop: 5,
                      fontWeight: 'bold',
                    }}
                  >
                    {(fhMetrics.working_capital_delta || 0) > 0
                      ? `+${formattedCurrency(fhMetrics.working_capital_delta)}`
                      : formattedCurrency(fhMetrics.working_capital_delta || 0)}
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{
                      color: (fhMetrics.working_capital_delta || 0) > 0 ? '#d32f2f' : '#2e7d32',
                      display: 'block',
                    }}
                  >
                    {(fhMetrics.working_capital_delta || 0) > 0
                      ? '⚠️ Saiu mais fiado novo do que entrou de contas velhas'
                      : '✅ Entrou mais dinheiro de contas antigas do que saiu fiado'}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Typography variant="caption" color="textSecondary">
                    PRAZO NOVO VS. RESGATE
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{
                      color: Number(fhMetrics.expansion_ratio ?? 1) <= 1.05 ? '#2e7d32' : '#d32f2f',
                      marginTop: 5,
                    }}
                  >
                    {Number(fhMetrics.expansion_ratio ?? 1).toFixed(2)}x
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Fiado Novo ÷ Fiado Recebido (Meta ≤ 1.05)
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Typography variant="caption" color="textSecondary">
                    RECUPERAÇÃO DE COBRANÇAS
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{
                      color: Number(fhMetrics.collection_efficiency ?? 0) >= 0.90 ? '#2e7d32' : '#d32f2f',
                      marginTop: 5,
                    }}
                  >
                    {(Number(fhMetrics.collection_efficiency ?? 0) * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Contas pagas no prazo (Meta ≥ 90%)
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Typography variant="caption" color="textSecondary">
                    ORIGEM DO CAIXA
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{
                      color: Number(fhMetrics.past_dependence_rate ?? 0) <= 0.45 ? '#2e7d32' : '#d32f2f',
                      marginTop: 5,
                    }}
                  >
                    {(Number(fhMetrics.past_dependence_rate ?? 0) * 100).toFixed(1)}%
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Caixa vindo de dívidas velhas (Meta ≤ 45%)
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* ZONE 5: CASH FLOW & FORECASTS */}
          <Grid item xs={12}>
            <Paper id="tour-planning-cashflow" style={{ padding: 20 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <Typography variant="h6" style={{ color: '#555' }}>
                  Previsões para o Mês
                </Typography>
                <Chip
                  label="PREVISÃO"
                  size="small"
                  style={{
                    backgroundColor: '#fff3e0',
                    color: '#1976d2',
                    fontWeight: 'bold',
                  }}
                />
              </div>
              <Divider style={{ marginBottom: 20 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    CONTAS ANTIGAS A RECEBER
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ color: '#1976d2', fontWeight: 'bold', marginTop: 5 }}
                  >
                    {formattedCurrency(forecastedActiveReceivables)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Promissórias e parcelas que vencem este mês
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    MESMO MÊS DO ANO PASSADO
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ color: '#7b1fa2', fontWeight: 'bold', marginTop: 5 }}
                  >
                    {formattedCurrency(nextData?.last_year_summary?.exactly_month?.cash_flow?.total_revenue || 0)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Quanto entrou no caixa no mesmo período do ano anterior
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    VENDAS PREVISTAS PARA O MÊS
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{ color: '#388e3c', fontWeight: 'bold', marginTop: 5 }}
                  >
                    {formattedCurrency(forecastedNewSales)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Estimativa de novas vendas calculada pelo seu histórico
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">
                    LUCRO PREVISTO DAS VENDAS
                  </Typography>
                  <Typography
                    variant="h5"
                    style={{
                      color: forecastedMarginValue > 0 ? '#388e3c' : '#d32f2f',
                      fontWeight: 'bold',
                      marginTop: 5,
                    }}
                  >
                    {formattedCurrency(forecastedMarginValue)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Margem de Lucro: {forecastedMarginPercent.toFixed(1)}%
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* ZONE 3 CONTINUED: TREND (Full Width Bottom) */}
          <Grid item xs={12}>
            <Paper id="tour-planning-trend" style={{ padding: 20 }}>
              <Typography variant="h6" gutterBottom style={{ color: '#555' }}>
                Evolução dos Últimos Meses (Histórico e Previsão)
              </Typography>
              <div style={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => `R$ ${v / 1000}k`} />
                    <RechartsTooltip
                      formatter={(value) => formattedCurrency(value)}
                    />
                    <Legend />

                    {/* Stacked Bars for Costs & Withdrawals */}
                    <Bar
                      dataKey="actualCosts"
                      name="Contas Pagas da Loja (Real)"
                      fill="#d32f2f"
                      stackId="a"
                      barSize={20}
                    />
                    <Bar
                      dataKey="actualWithdrawals"
                      name="Retiradas dos Donos (Real)"
                      fill="#f57b00b0"
                      stackId="a"
                      barSize={20}
                    />
                    <Bar
                      dataKey="forecastWithdrawals"
                      name="Retiradas Previstas"
                      fill="#f57b0060"
                      stroke="#f57b00"
                      strokeDasharray="2 2"
                      stackId="a"
                      barSize={20}
                    />

                    {/* Actual Lines (Solid) */}
                    <Line
                      type="monotone"
                      dataKey="actualRevenue"
                      name="Dinheiro que Entrou (Real)"
                      stroke="#1976d2"
                      strokeWidth={3}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      connectNulls={false}
                    />

                    {/* Forecast Lines (Dashed) */}
                    <Line
                      type="monotone"
                      dataKey="forecastRevenue"
                      name="Dinheiro Previsto a Entrar"
                      stroke="#1976d2"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 2 }}
                      connectNulls={true}
                    />
                    <Line
                      type="monotone"
                      dataKey="forecastCosts"
                      name="Gastos Previstos"
                      stroke="#d32f2f"
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      dot={{ r: 2 }}
                      connectNulls={true}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper id="tour-planning-ticket-histogram" style={{ padding: 20 }}>
              <Typography variant="h6" gutterBottom style={{ color: '#555' }}>
                Valor Médio por Compra (Total Vendido vs. Pago na Hora)
              </Typography>
              <div style={{ height: 300, width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis tickFormatter={(v) => `R$ ${v}`} />
                    <RechartsTooltip
                      cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                      formatter={(value) => formattedCurrency(value)}
                    />
                    <Legend />
                    <Bar
                      dataKey="actualTicket"
                      name="Valor Médio Total por Compra"
                      fill="#7b1fa2"
                      radius={[4, 4, 0, 0]}
                      barSize={40}
                    />
                    <Bar
                      dataKey="actualTicketEffective"
                      name="Valor Médio Pago na Hora"
                      fill="#1976d2"
                      radius={[4, 4, 0, 0]}
                      barSize={40}
                    />
                    <Line
                      type="monotone"
                      dataKey="forecastTicket"
                      name="Meta por Compra"
                      stroke="#7b1fa2"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 3 }}
                      connectNulls={true}
                    />
                    <Line
                      type="monotone"
                      dataKey="forecastTicketEffective"
                      name="Estimativa Pago na Hora"
                      stroke="#1976d2"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ r: 3 }}
                      connectNulls={true}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grid>
        </Grid>
      </div >
    </PageContent >
  );
};

export default Planejamento;
